import { useEffect, useState } from 'react'
import { Toast } from 'antd-mobile'
import styles from './index.scss'
import goTo from '@/libs/functions/goTo'
import {
  addCoupon,
  getMarketingRecommendList,
  MarketingRecommendItem,
} from '@/libs/services'

interface MarketingRecommendListProps {
  productCode?: string
  sceneCode?: 'payResult' | 'orderDetail' | 'unsubscribeResult'
  className?: string
}

// 价格/次数区域统一渲染
// 优先级: price(有值时按exchangeType渲染) → availableNum(权益剩余次数) → 空占位
// exchangeType: 1=现金 2=积分 3=权益 4=客户权益/积分 5=现金+积分 6=项目下单 7=龙企额度 8=现金+客户权益
const renderPriceOrStock = (item: MarketingRecommendItem) => {
  const { price, exchangeType, exchangePriceSuffix, currencySymbol, availableNum, equityUnitName, recommendType } = item

  // 有 price 时按 exchangeType 渲染价格
  if (price != null) {
    const symbol = currencySymbol || '¥'
    const suffix = exchangePriceSuffix === 1 ? <span className={styles.priceSuffix}>起</span> : null

    // 现金场景: 1(现金) / 8(现金+客户权益) → 展示 ¥xxx.xx
    if ([1, 8].includes(exchangeType as number) || exchangeType == null) {
      return (
        <div className={styles.itemPrice}>
          <span className={styles.priceSymbol}>{symbol}</span>
          <span className={styles.priceValue}>{Number(price).toFixed(2)}</span>
          {suffix}
        </div>
      )
    }

    // 积分场景: 2(SaaS积分) / 4(客户权益/积分) → 展示 xxx + 积分
    if ([2, 4].includes(exchangeType as number)) {
      return (
        <div className={styles.itemPrice}>
          <span className={styles.priceValue}>{price}</span>
          <span className={styles.priceUnit}>积分</span>
          {suffix}
        </div>
      )
    }

    // 权益/其他场景: 3(权益) / 6(项目下单) / 7(龙企额度) → 兜底按现金展示
    return (
      <div className={styles.itemPrice}>
        <span className={styles.priceSymbol}>{symbol}</span>
        <span className={styles.priceValue}>{Number(price).toFixed(2)}</span>
        {suffix}
      </div>
    )
  }

  // 无 price 但有 availableNum → 展示剩余次数/件数
  if (availableNum != null) {
    // coupon 固定用"张"，equityUnitName 优先用接口返回，兜底"次"
    const unit = recommendType === 'coupon' ? '张' : (equityUnitName || '次')
    return (
      <div className={styles.itemStock}>
        剩{availableNum}{unit}
      </div>
    )
  }

  return null
}

// 标题图标（host.png 矢量化后的火焰 SVG）
const DefaultTitleIcon = () => (
  <svg
    className={styles.titleIcon}
    viewBox="0 0 36 36"
    xmlns="http://www.w3.org/2000/svg"
  >
    <path
      fill="currentColor"
      fillOpacity="0.8588235294117647"
      d="M 13 5 Q 20.6 5.2 24 10.5 L 25.5 15 Q 27.3 11.3 29 14.5 L 30 25.5 L 24.5 33 L 22.5 34 Q 23.7 24.3 17.5 22 Q 17.4 27.4 14 29.5 L 14 33 L 11.5 33 L 7 28.5 Q 4.7 25.3 6 18.5 L 13 9.5 L 13 5 Z"
    />
  </svg>
)

// 根据规则表和布局样式确定按钮文案
const getButtonText = (
  item: MarketingRecommendItem,
  listStyle?: string,
): string => {
  const isTwoColumn = listStyle === 'twoColumn'
  switch (item?.recommendType) {
    case 'product':
      return isTwoColumn ? '购买' : '立即购买'
    case 'coupon':
      // 规则：未领取（未返回redirectUrl）显示"立即领取/领取"，已领取（有redirectUrl）显示"立即使用/去使用"
      return item?.redirectUrl
        ? isTwoColumn
          ? '去使用'
          : '立即使用'
        : isTwoColumn
        ? '领取'
        : '立即领取'
    case 'equity':
      return isTwoColumn ? '去使用' : '立即使用'
    case 'equity_package':
      return isTwoColumn ? '去使用' : '立即使用'
    case 'draw_activity':
      return '去查看'
    case 'point_section':
      return '去查看'
    case 'custom':
      return '去查看'
    default:
      return isTwoColumn ? '购买' : '立即购买'
  }
}

// 根据规则表确定是否展示
const shouldDisplay = (item: MarketingRecommendItem): boolean => {
  switch (item?.recommendType) {
    case 'equity':
    case 'equity_package':
      return !!(item && Object.keys(item).length > 0)
    case 'point_section':
      return true
    default:
      return true
  }
}

// 单列布局 — 横向卡片
const SingleRowItem = ({
  item,
  listStyle,
  claiming,
  onClick,
}: {
  item: MarketingRecommendItem
  listStyle?: string
  claiming: boolean
  onClick: () => void
}) => {
  const priceNode = renderPriceOrStock(item)
  return (
    <div
      className={`${styles.singleItem} ${
        !priceNode ? styles.singleItemNoPrice : ''
      }`}
      onClick={onClick}
    >
      <div className={styles.singleItemImg}>
        {item.displayImage ? (
          <img
            src={item.displayImage}
            alt={item.displayName}
            onError={(e) => {
              e.currentTarget.style.display = 'none'
            }}
          />
        ) : (
          <div className={styles.imgPlaceholder} />
        )}
      </div>
      <div className={styles.singleItemInfo}>
        <div className={styles.singleItemTop}>
          <div className={styles.singleItemName}>{item.displayName}</div>
          {priceNode}
        </div>
      <div className={styles.singleItemBottom}>
        <div className={styles.singleItemDesc}>{item.displayDesc}</div>
        <span className={styles.singleActionBtn}>
          {claiming ? '领取中...' : getButtonText(item, listStyle)}
        </span>
      </div>
    </div>
  </div>
  )
}

// 双列布局 — 竖向卡片
const DoubleColumnItem = ({
  item,
  listStyle,
  claiming,
  onClick,
}: {
  item: MarketingRecommendItem
  listStyle?: string
  claiming: boolean
  onClick: () => void
}) => (
  <div className={styles.gridItem} onClick={onClick}>
    <div className={styles.gridItemImg}>
      {item.displayImage ? (
        <img
          src={item.displayImage}
          alt={item.displayName}
          onError={(e) => {
            e.currentTarget.style.display = 'none'
          }}
        />
      ) : (
        <div className={styles.imgPlaceholder} />
      )}
    </div>
    <div className={styles.gridItemInfo}>
      <div className={styles.gridItemName}>{item.displayName}</div>
      <div className={styles.gridItemBottom}>
        {renderPriceOrStock(item) || <div className={styles.gridItemPlaceholder} />}
        <span className={styles.gridActionBtn}>
          {claiming ? '领取中...' : getButtonText(item, listStyle)}
        </span>
      </div>
    </div>
  </div>
)

export default function MarketingRecommendList(
  props: MarketingRecommendListProps,
) {
  const { productCode, sceneCode, className } = props
  const [listStyle, setListStyle] = useState<string>('list')
  const [h5Title, setH5Title] = useState<string>('商品推荐')
  const [recommendList, setRecommendList] = useState<MarketingRecommendItem[]>(
    [],
  )
  // 正在领取的优惠券 relationCode，用于防重复点击
  const [claimingCode, setClaimingCode] = useState<string | null>(null)

  const fetchData = async () => {
    // productCode为空时不发起请求，避免首次渲染（订单详情未加载完）时产生无效请求
    if (!productCode) {
      setRecommendList([])
      return
    }
    try {
      const res = await getMarketingRecommendList({ productCode, sceneCode })
      setListStyle(res?.listStyle || 'list')
      setH5Title(res?.h5Title || '商品推荐')
      const filtered = (res?.recommendList || []).filter(shouldDisplay)
      setRecommendList(filtered)
    } catch {
      setListStyle('list')
      setRecommendList([])
    }
  }

  useEffect(() => {
    fetchData()
  }, [productCode, sceneCode])

  // 领取优惠券
  const handleClaimCoupon = async (item: MarketingRecommendItem) => {
    const couponCode = item?.relationCode
    if (!couponCode) {
      Toast.show({ content: '优惠券编码缺失' })
      return
    }
    setClaimingCode(couponCode)
    try {
      await addCoupon({ couponCode })
      Toast.show({ content: '领取成功' })
      // 领取成功后刷新列表，更新可领取状态
      await fetchData()
    } catch (err: any) {
      Toast.show({ content: err?.msg || err?.message || '领取失败' })
    } finally {
      setClaimingCode(null)
    }
  }

  // 统一点击处理
  const handleItemClick = (item: MarketingRecommendItem) => {
    // 优惠券未返回 redirectUrl 时才调用领取接口；返回了 url 直接走跳转
    if (
      item?.recommendType === 'coupon' &&
      !item?.redirectUrl &&
      (item?.availableNum == null || item?.availableNum > 0) &&
      item?.relationCode
    ) {
      const key = item?.relationCode || ''
      if (claimingCode === key) return // 防重复点击
      handleClaimCoupon(item)
      return
    }
    // 其他场景（优惠券已领取/其他类型）走跳转逻辑
    if (item?.redirectUrl) {
      // 兼容接口返回的 redirectUrl 格式差异：
      // 1. 外链（http 开头）：原样跳转，goTo 会用 window.location
      // 2. 带 /# 前缀的 hash 路由：去掉 /#，避免 history.push 变成 /#/#/xxx 匹配失败
      // 3. 普通内部路径：原样走 history push
      const rawUrl = item.redirectUrl
      const normalizedUrl = rawUrl.startsWith('http')
        ? rawUrl
        : rawUrl.replace(/^\/#\//, '/')
      goTo(normalizedUrl)
    }
  }

  if (!recommendList?.length) {
    return null
  }

  return (
    <div
      className={`${styles.recommendList} ${className || ''}`}
    >
      <div className={styles.title}>
        <DefaultTitleIcon />
        <span>{h5Title}</span>
      </div>
      {listStyle === 'twoColumn' ? (
        <div className={styles.gridList}>
          {recommendList.map((item, index) => {
            const key = `${item.relationCode || item.displayName}_${index}`
            return (
              <DoubleColumnItem
                key={key}
                item={item}
                listStyle={listStyle}
                claiming={claimingCode === (item?.relationCode || '')}
                onClick={() => handleItemClick(item)}
              />
            )
          })}
        </div>
      ) : (
        <div className={styles.singleList}>
          {recommendList.map((item, index) => {
            const key = `${item.relationCode || item.displayName}_${index}`
            return (
              <SingleRowItem
                key={key}
                item={item}
                listStyle={listStyle}
                claiming={claimingCode === (item?.relationCode || '')}
                onClick={() => handleItemClick(item)}
              />
            )
          })}
        </div>
      )}
    </div>
  )
}
